import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api, { ACCESS_TOKEN_KEY } from '@/services/api';
import { setUser } from '@/store/slices/authSlice';
import type { RootState } from '@/store';

/**
 * useAuthBootstrap — on app mount, if a token exists in localStorage but Redux
 * has no user (e.g. page refresh), fetches /auth/me and hydrates the Redux store.
 */
export function useAuthBootstrap() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);

    if (!token) {
      setBootstrapping(false);
      return;
    }

    if (user) {
      setBootstrapping(false);
      return;
    }

    // Token exists but Redux is empty — hydrate from /auth/me
    api
      .get('/auth/me')
      .then((res) => {
        const payload = res.data?.data ?? res.data ?? {};
        dispatch(
          setUser({
            id: payload.id,
            name: payload.name,
            email: payload.email,
            platform_role: payload.platform_role,
            permissions: payload.permissions ?? [],
          }),
        );
      })
      .catch(() => {
        // Token expired/invalid — tokens already removed by api.ts 401 interceptor
      })
      .finally(() => {
        setBootstrapping(false);
      });
  }, [dispatch, user]);

  return { bootstrapping };
}
